import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { type ResolvedDateTimeFormat, toDate } from "@/shared/datetime";
import { activeDateTimeFormat } from "@/shared/datetime/formatters";
import {
  CustomFieldRecord,
  CustomFieldType,
} from "@/shared/domain/customFields";
import { formatBoolean } from "@/shared/text";

// `dayjs.tz.guess()` needs utc + timezone.
dayjs.extend(utc);
dayjs.extend(timezone);

/** The viewer's IANA time zone, which the Timestamp input edits in and shows as its label. */
export function guessTimezone(): string {
  return dayjs.tz.guess();
}

/**
 * Wire value for a Timestamp draft. Guards against the invalid dayjs the
 * DateTimePicker hands `onChange` for an out-of-range or half-typed date
 * (e.g. Feb 30): a truthy-but-invalid dayjs whose `.toISOString()` throws
 * `RangeError: Invalid time value`. An invalid input emits `null`, which the
 * timestamp schema rejects — disabling commit rather than crashing on it.
 */
export function timestampToWireValue(next: dayjs.Dayjs | null): string | null {
  return next && next.isValid() ? next.toISOString() : null;
}

/**
 * Renders a custom-field value for the values table, formatted by the field's
 * declared type. Callers pass only non-null values (cleared fields aren't
 * rendered). The table renders Enum values as chips, so this branch is a
 * fallback. Timestamps render through `dateTimeFormat`, read in its zone with
 * that zone's short name appended; it defaults to the active format, and a
 * component passes the one from `useDateTimeFormat` so the value follows the
 * preference as it loads or changes. A Timestamp value that isn't a parseable
 * string or number renders as `String(value)`.
 */
export function formatCustomFieldValue(
  field: CustomFieldRecord,
  value: unknown,
  dateTimeFormat: ResolvedDateTimeFormat = activeDateTimeFormat(),
): string {
  switch (field.field_type) {
    case CustomFieldType.Boolean:
      return formatBoolean(Boolean(value));
    case CustomFieldType.Timestamp: {
      const date =
        typeof value === "string" || typeof value === "number"
          ? toDate(value)
          : null;
      return date === null
        ? String(value)
        : dateTimeFormat.format(date, { timeZone: true });
    }
    case CustomFieldType.Number:
      return typeof value === "number" ? value.toLocaleString() : String(value);
    case CustomFieldType.String:
    case CustomFieldType.Enum:
    default:
      return String(value);
  }
}
